import { useEffect, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
// Side-effect import: patches signature_pad v2's crash on multi-touch and on a
// clear() that lands mid-stroke. Must be imported before any pad is built.
import '../../../lib/signaturePad';

interface SignatureFieldProps {
  /** Called with the trimmed PNG data URL after each stroke, or null on clear. */
  onChange: (dataUrl: string | null) => void;
  /** A signature is already on file (resume / reopen): show a hint. */
  hasPrior?: boolean;
}

/**
 * Inline signature pad for a `signature` form field. Emits a base64 PNG data
 * URL the renderer stores via the file pipeline. The canvas resolution is kept
 * in sync with its rendered size (signature_pad doesn't auto-resize) so the pen
 * tip stays under the finger / cursor. The pad stays white-on-charcoal in every
 * theme so the captured image survives download / print.
 */
export function SignatureField({ onChange, hasPrior }: SignatureFieldProps) {
  const padRef = useRef<SignatureCanvas | null>(null);

  useEffect(() => {
    const pad = padRef.current;
    if (!pad) return;
    const canvas = pad.getCanvas();
    function resize() {
      const p = padRef.current;
      if (!p) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      if (w === 0 || h === 0) return;
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
      p.clear();
      onChange(null);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    window.addEventListener('orientationchange', resize);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', resize);
    };
    // Sized once on mount + on resize; onChange is stable enough for this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function emit() {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return onChange(null);
    onChange(pad.getTrimmedCanvas().toDataURL('image/png'));
  }
  function clear() {
    padRef.current?.clear();
    onChange(null);
  }

  return (
    <div className="mt-1.5">
      <div className="relative overflow-hidden rounded-token-sm border border-line-strong/20 bg-white">
        <SignatureCanvas
          ref={padRef}
          penColor="#0f172a"
          onEnd={emit}
          canvasProps={{ className: 'block h-40 w-full touch-none' }}
        />
        <button
          type="button"
          onClick={clear}
          className="absolute bottom-1 right-2 rounded-token-sm px-2 py-0.5 text-xs text-slate-500 hover:text-slate-800"
        >
          Clear
        </button>
      </div>
      {hasPrior ? (
        <p className="mt-1 text-xs text-fg-dim">Already signed. Sign again to replace it.</p>
      ) : null}
    </div>
  );
}
